import { useCallback, useEffect, useMemo, useState } from 'react';
import { metaAlignmentService } from '../../backend/services/metaAlignment';
import type { MetaAlignmentFilters, MetaAlignmentResult } from '../../shared/types/metaAlignment';

export const useMetaAlignment = (initialFilters: MetaAlignmentFilters = {}) => {
  const [filters, setFilters] = useState<MetaAlignmentFilters>(initialFilters);
  const [data, setData] = useState<MetaAlignmentResult | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (effectiveFilters: MetaAlignmentFilters) => {
    try {
      setLoading(true);
      setError(null);
      const result = await metaAlignmentService.fetchAlignment(effectiveFilters);
      setData(result);
    } catch (err) {
      console.error('Failed to load Meta alignment data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load Meta alignment data');
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    fetchData(filters);
  }, [fetchData, filters]);

  useEffect(() => {
    fetchData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const setFilterValue = useCallback(<K extends keyof MetaAlignmentFilters>(key: K, value: MetaAlignmentFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({});
  }, []);

  const result = useMemo(() => ({ data, filters, loading, error }), [data, filters, loading, error]);

  return {
    ...result,
    setFilters,
    setFilterValue,
    resetFilters,
    refresh,
  };
};
